import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Grid } from "@mui/material";
import {
  Btn,
  IconBtnBase,
  InputController,
  RadioGroupController,
  ResourcesList,
  setProgress,
  SwitchController,
} from "Components";
import Yup from "Utils/Yup";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { SEASON4SETTINGS } from "Constants/QueriesKeys";
import { useQuery } from "react-query";
import { http } from "Utils/Http/Http";
import { toast } from "react-toastify";
import { BANNER_SOURCES } from "./bannerSources";
import BannerPreviewDialog from "./BannerPreviewDialog";

const UPDATE_SEASON_4 = "UPDATE_SEASON_4";

// Mirrors App\Support\YouTubeUrl::toEmbed on the backend, which is what
// actually decides the stored value. Keep the two in step: anything this
// rejects can never be saved, so being stricter than the backend would block
// links the API would happily accept (m.youtube.com, a bare video id, ...).
const YOUTUBE_URL =
  /(?:youtube\.com\/(?:embed|live|shorts)\/[A-Za-z0-9_-]{6,})|(?:youtube(?:-nocookie)?\.com\/watch\?(?:[^#]*&)?v=[A-Za-z0-9_-]{6,})|(?:youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{6,})|(?:youtu\.be\/[A-Za-z0-9_-]{6,})|(?:^[A-Za-z0-9_-]{11}$)/i;

// Must stay in step with Setting::SEASON_4_BANNERS on the backend.
const BANNER_DEFAULT = "default";
const BANNER_OPTIONS = [
  BANNER_DEFAULT,
  "banner_1",
  "banner_2",
  "banner_3",
  "banner_4",
];

const schema = Yup.object({
  season_4_enabled: Yup.boolean(),
  season_4_active_banner: Yup.string().oneOf(BANNER_OPTIONS),
  season_4_live_video_url: Yup.string()
    .trim()
    .matches(YOUTUBE_URL, { message: "youtubeUrl", excludeEmptyString: true })
    .nullable(),
});

const useStyles = makeStyles((theme) => ({
  h4Text: {
    textTransform: "capitalize",
    fontSize: "18px",
    color: "rgb(3, 166, 90)",
    fontWeight: "500",
  },
  // The banner labels are image previews, so the radio has to sit at the top
  // of the row rather than centred against a tall block.
  bannerChoices: {
    "& .MuiFormControlLabel-root": {
      alignItems: "flex-start",
      marginBottom: "12px",
    },
    "& .MuiRadio-root": {
      paddingTop: "10px",
    },
  },
}));

/**
 * A banner choice, shown with both language versions so the admin can see
 * exactly what will go live before saving.
 *
 * The images are the originals served by the public site (see bannerSources.js),
 * scaled down by the browser for the thumbnail. Clicking one opens it full size
 * instead of selecting the option — picking a banner stays a deliberate click on
 * the radio itself.
 */
const BannerOption = ({ value, label, onPreview }) => {
  const { t } = useTranslation();
  const groups = BANNER_SOURCES[value] ?? [];
  const locales = ["ar", "en"];

  return (
    <div className="py-2">
      <div className="pb-1 font-medium">{label}</div>

      {groups.map((group, index) => (
        <div key={group.pageKey ?? index} className="pb-2">
          {group.pageKey && (
            <div className="pb-1 text-[11px] text-gray-500">
              {t(`admin.cms.season4.banners.pages.${group.pageKey}`)}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {locales.map((locale) => {
              const caption = locale.toUpperCase();
              const alt = `${label} — ${caption}`;

              return (
                <figure key={locale} className="m-0">
                  <button
                    type="button"
                    // Inside a radio label, so a bare click would also select
                    // the option. Zooming must not commit a choice.
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onPreview({ src: group[locale], alt });
                    }}
                    title={t("admin.cms.season4.banners.zoomHint")}
                    className="group relative block cursor-zoom-in border-0 bg-transparent p-0"
                  >
                    <img
                      src={group[locale]}
                      alt={alt}
                      loading="lazy"
                      decoding="async"
                      width={260}
                      className="block h-auto w-[260px] rounded border border-gray-200"
                    />
                    <span className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100">
                      <ZoomInIcon fontSize="small" />
                    </span>
                  </button>
                  <figcaption className="pt-0.5 text-center text-[11px] text-gray-500">
                    {caption}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const Season4 = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [enabled, setEnabled] = useState(true);
  // Until the GET lands, the form is showing useForm's defaults, not the saved
  // state. Submitting then would quietly write those defaults over whatever the
  // admin had configured, so saving stays locked until we have real values.
  const [loadedFromServer, setLoadedFromServer] = useState(false);
  // The banner currently open full size, or null. { src, alt }
  const [preview, setPreview] = useState(null);

  const settingsQuery = useQuery(
    [SEASON4SETTINGS],
    () =>
      // The admin read, not the public one: the public endpoint withholds the
      // video URL while Season 4 is off, and the form must still show it.
      http.get(`/api/v1/admin/settings`, {}).then(({ data }) => {
        setEnabled(false);
        setLoadedFromServer(true);
        return reset({
          season_4_enabled: Boolean(data?.data?.season_4_enabled),
          season_4_active_banner:
            data?.data?.season_4_active_banner ?? BANNER_DEFAULT,
          season_4_live_video_url: data?.data?.season_4_live_video_url ?? "",
        });
      }),
    {
      enabled: enabled ? true : false,
    }
  );

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      season_4_enabled: false,
      season_4_active_banner: BANNER_DEFAULT,
      season_4_live_video_url: "",
    },
  });
  const { control, handleSubmit, reset } = form;

  const handleFormSubmit = async (values) => {
    setProgress(UPDATE_SEASON_4, true);
    try {
      await http.post(`/api/v1/admin/settings`, {
        season_4_enabled: Boolean(values.season_4_enabled),
        season_4_active_banner: values.season_4_active_banner || BANNER_DEFAULT,
        // An empty field clears the video rather than saving "".
        season_4_live_video_url: values.season_4_live_video_url?.trim() || null,
      });
      toast.success(t("toast.success.updatedSuccessfully"));
      settingsQuery.refetch();
    } catch (res) {
      toast.error(res?.response?.data?.message);
    }
    setProgress(UPDATE_SEASON_4, false);
  };

  return (
    <ResourcesList
      headerProps={{
        title: t("admin.cms.season4.season4"),
        EndSide: (
          <div className="flex">
            <IconBtnBase
              onClick={() => {
                settingsQuery.refetch();
              }}
              size="large"
            >
              <RefreshIcon />
            </IconBtnBase>
          </div>
        ),
      }}
      Section={
        <div className="mt-8">
          <Grid container>
            <Grid item xs={12}>
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="space-y-3 py-5"
              >
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <h3 className={classes.h4Text}>
                      {t("admin.cms.season4.settings")}
                    </h3>
                  </AccordionSummary>
                  <Divider style={{ margin: "10px 0" }} />
                  <AccordionDetails>
                    <Grid container spacing={2} className="py-2">
                      <Grid item xs={12} md={5} lg={4} xl={3}>
                        <h6
                          className="labelForm"
                          style={{ marginBottom: "0px" }}
                        >
                          {t("admin.cms.season4.status")}
                        </h6>
                      </Grid>
                      <Grid item xs={12} md={7} lg={8} xl={9}>
                        <SwitchController
                          name="season_4_enabled"
                          control={control}
                          defaultValue={false}
                        />
                        <p className="text-xs text-gray-500">
                          {t("admin.cms.season4.statusHint")}
                        </p>
                      </Grid>

                      <Grid item xs={12} md={5} lg={4} xl={3}>
                        <h6
                          className="labelForm"
                          style={{ marginBottom: "0px" }}
                        >
                          {t("admin.cms.season4.liveVideoUrl")}
                        </h6>
                      </Grid>
                      <Grid item xs={12} md={7} lg={8} xl={9}>
                        <InputController
                          name="season_4_live_video_url"
                          control={control}
                          size="small"
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                        <p className="text-xs text-gray-500">
                          {t("admin.cms.season4.liveVideoUrlHint")}
                        </p>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                  >
                    <h3 className={classes.h4Text}>
                      {t("admin.cms.season4.activeBanner")}
                    </h3>
                  </AccordionSummary>
                  <Divider style={{ margin: "10px 0" }} />
                  <AccordionDetails>
                    <Grid container spacing={2} className="py-2">
                      <Grid item xs={12} md={5} lg={4} xl={3}>
                        <h6
                          className="labelForm"
                          style={{ marginBottom: "0px" }}
                        >
                          {t("admin.cms.season4.activeBanner")}
                        </h6>
                      </Grid>
                      <Grid item xs={12} md={7} lg={8} xl={9}>
                        <RadioGroupController
                          name="season_4_active_banner"
                          control={control}
                          className={classes.bannerChoices}
                          defaultValue={BANNER_DEFAULT}
                          options={BANNER_OPTIONS.map((value) => ({
                            value,
                            label: (
                              <BannerOption
                                value={value}
                                label={t(`admin.cms.season4.banners.${value}`)}
                                onPreview={setPreview}
                              />
                            ),
                          }))}
                        />
                        <p className="text-xs text-gray-500">
                          {t("admin.cms.season4.activeBannerHint")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("admin.cms.season4.banners.zoomHint")}
                        </p>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <div className="flex flex-col gap-2 pt-3">
                  {!loadedFromServer && (
                    <p className="text-xs text-gray-500">
                      {settingsQuery.isError
                        ? t("admin.cms.season4.loadFailed")
                        : t("admin.cms.season4.loading")}
                    </p>
                  )}
                  <div className="flex">
                    <Btn
                      fullWidth={false}
                      type="submit"
                      name={UPDATE_SEASON_4}
                      // Only ever pass disabled when it should be true: BtnBase
                      // spreads props after its own disabled={isLoading}, so a
                      // literal false would re-enable the button mid-save.
                      {...(loadedFromServer ? {} : { disabled: true })}
                    >
                      {t("common.submit")}
                    </Btn>
                  </div>
                </div>
              </form>
            </Grid>
          </Grid>

          <BannerPreviewDialog
            preview={preview}
            onClose={() => setPreview(null)}
          />
        </div>
      }
    />
  );
};

export default Season4;
